import {
  getAssetUrl,
  mapContentfulGallery,
  mapContentfulSponsors,
  resolveContentfulAsset,
} from "./resolveContentfulAsset";
import {
  MEET_FAQS,
  MEET_GALLERY_PLACEHOLDERS,
  MEET_HIGHLIGHTS,
  MEET_QUICK_STATS,
  MEET_SOCIAL,
  MEET_SPONSOR_PLACEHOLDERS,
  MEET_UI,
  MEET_VENUE,
  MEET_VISIT,
} from "components/MeetPage/MeetExperience/meetPageContent";

export const DEFAULT_MEET_REGISTRATION_URL =
  "https://members.usagym.org/app/sanctionDetail.html?id=90376";

/** Ensures register buttons always have a valid external URL. */
export const resolveMeetRegistrationUrl = (raw) => {
  const value = typeof raw === "string" ? raw.trim() : "";
  if (!value) return DEFAULT_MEET_REGISTRATION_URL;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value}`;
};

const parseJsonArray = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

const normalizeHighlights = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return MEET_HIGHLIGHTS;

  return items
    .map((item) => ({
      title: item.title || item.name || "",
      text: item.text || item.description || "",
    }))
    .filter((item) => item.title && item.text);
};

const normalizeQuickStats = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return MEET_QUICK_STATS;

  return items
    .map((item) => ({
      value: item.value || "",
      label: item.label || "",
    }))
    .filter((item) => item.value && item.label);
};

const normalizeFaqs = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return MEET_FAQS;

  return items
    .map((item) => ({
      q: item.q || item.question || "",
      a: item.a || item.answer || "",
    }))
    .filter((item) => item.q && item.a);
};

const normalizeSponsors = (sponsorsField, includes) => {
  const fromMedia = mapContentfulSponsors(sponsorsField, includes);
  if (fromMedia.length) return fromMedia;

  return MEET_SPONSOR_PLACEHOLDERS.map((name) => ({
    name,
    logoUrl: null,
    url: null,
  }));
};

const normalizeMeetFees = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return [];

  return items
    .map((item) => ({
      label: item.label || item.name || "",
      amount: item.amount || item.value || "",
    }))
    .filter((item) => item.label && item.amount);
};

const normalizeAdmissionPrices = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return [];

  return items
    .map((item) => ({
      category: item.category || "",
      ageRange: item.ageRange || item.age || "",
      daily: item.daily || "",
    }))
    .filter((item) => item.category && item.daily);
};

const normalizeRegistrationSteps = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return [];

  return items
    .map((item, index) => ({
      step: Number(item.step) || index + 1,
      title: item.title || "",
      items: Array.isArray(item.items)
        ? item.items.filter(Boolean)
        : item.text
          ? [item.text]
          : [],
    }))
    .filter((item) => item.title && item.items.length);
};

const normalizeContactsList = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return [];

  return items
    .map((item) => ({
      name: item.name || "",
      role: item.role || item.title || "",
      email: item.email || "",
      phone: item.phone || "",
    }))
    .filter((item) => item.name);
};

const normalizeVisitHighlights = (raw) => {
  const items = parseJsonArray(raw);
  if (!items?.length) return MEET_VISIT.highlights;

  return items
    .map((item) => ({
      title: item.title || item.name || "",
      text: item.text || item.description || "",
    }))
    .filter((item) => item.title && item.text);
};

const normalizeVisitPlaces = (raw, fallback = []) => {
  const items = parseJsonArray(raw);
  const places = (items?.length ? items : fallback)
    .map((item) => ({
      name: item.name || item.title || "",
      address: item.address || item.street || "",
      distance: item.distance || "",
      note: item.note || item.text || item.description || "",
      url: item.url || item.website || "",
      cuisine: item.cuisine || item.type || "",
      isPartner: Boolean(item.isPartner || item.partner),
      directionsUrl: item.directionsUrl || "",
    }))
    .filter((item) => item.name);

  return [...places].sort((a, b) => Number(b.isPartner) - Number(a.isPartner));
};

const buildVisitSection = (fields, includes) => {
  const visitImageAsset = resolveContentfulAsset(fields?.visitImage, includes);
  const imageUrl = getAssetUrl(visitImageAsset);
  const imageAlt =
    visitImageAsset?.fields?.description?.trim() ||
    visitImageAsset?.fields?.title?.trim() ||
    "Historic Bardstown, Kentucky";

  return {
    heading: MEET_VISIT.heading,
    tagline: MEET_VISIT.tagline,
    intro: MEET_VISIT.intro,
    highlights: normalizeVisitHighlights(fields?.visitHighlights),
    hotelsHeading: MEET_VISIT.hotelsHeading,
    hotelsIntro: MEET_VISIT.hotelsIntro,
    hotels: normalizeVisitPlaces(fields?.visitHotels, MEET_VISIT.hotels),
    restaurantsHeading: MEET_VISIT.restaurantsHeading,
    restaurantsIntro: MEET_VISIT.restaurantsIntro,
    restaurants: normalizeVisitPlaces(fields?.visitRestaurants, MEET_VISIT.restaurants),
    tourismUrl: fields?.visitTourismUrl?.trim() || MEET_VISIT.tourismUrl,
    imageUrl: imageUrl || null,
    imageAlt,
  };
};

const toMapQuery = (value) =>
  value.trim().replace(/\s+/g, "+").replace(/,+/g, ",");

const buildDirectionsUrl = (mapQuery) =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    mapQuery.replace(/\+/g, " ")
  )}`;

const normalizeVenue = (fields) => {
  const name = fields?.venueName?.trim() || MEET_VENUE.name;
  const street = fields?.venueStreet?.trim() || MEET_VENUE.street;
  const cityStateZip =
    fields?.venueCityStateZip?.trim() || MEET_VENUE.cityStateZip;
  const phone = fields?.venuePhone?.trim() || MEET_VENUE.phone;
  const mapQuery = toMapQuery(`${street}, ${cityStateZip}`.replace(/\s+/g, " "));
  const directionsUrl = buildDirectionsUrl(mapQuery);

  return {
    name,
    street,
    cityStateZip,
    phone,
    mapQuery,
    mapEmbedUrl: `https://maps.google.com/maps?q=${mapQuery}&output=embed`,
    directionsUrl,
  };
};

/**
 * Builds a single object used by MeetExperience from the lean Contentful field set.
 */
export const buildMeetPageData = (fields, includes) => {
  const heroAsset = resolveContentfulAsset(fields?.heroImage, includes);
  const gallery = mapContentfulGallery(fields?.gallery, includes);
  const sponsors = normalizeSponsors(fields?.sponsorLogos, includes);

  return {
    ...MEET_UI,

    quickStats: normalizeQuickStats(fields?.quickStats),
    highlights: normalizeHighlights(fields?.highlights),
    faqs: normalizeFaqs(fields?.faqs),
    gallery: gallery.length ? gallery : MEET_GALLERY_PLACEHOLDERS,
    sponsors,

    social: MEET_SOCIAL,

    heroImageUrl: getAssetUrl(heroAsset),
    registrationUrl: resolveMeetRegistrationUrl(fields?.registrationLink),
    venue: normalizeVenue(fields),

    meetFees: normalizeMeetFees(fields?.meetFees),
    admissionPrices: normalizeAdmissionPrices(fields?.admissionPrices),
    registrationSteps: normalizeRegistrationSteps(fields?.registrationSteps),
    contactsList: normalizeContactsList(fields?.contactsList),
    visit: buildVisitSection(fields, includes),
  };
};

export const normalizeGymnasticMeetEntry = (entry, includes) => {
  const pageData = buildMeetPageData(entry.fields, includes);
  return {
    ...entry,
    fields: {
      ...entry.fields,
      pageData,
    },
  };
};
